import { createFileRoute } from '@tanstack/react-router';
import { Boxes, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';

export const Route = createFileRoute('/_authenticated/_layout/components/typography')({
  component: TypographyPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function TypographyPage() {
  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8 space-y-8">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Font Family</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <p className="text-gray-900 mb-2">Primary Font: <span className="font-semibold">Montserrat</span></p>
              <p className="text-gray-900 font-['Montserrat']">The quick brown fox jumps over the lazy dog</p>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Typography Hierarchy</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Display (text-3xl font-bold text-gray-900)</h1>
                <p className="text-xs text-gray-500">Hero sections, marketing pages - 30px, 700 weight, primary text</p>
              </div>
              <Separator />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">H1 (text-2xl font-semibold text-gray-900)</h1>
                <p className="text-xs text-gray-500">Page titles - 24px, 600 weight, primary text</p>
              </div>
              <Separator />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">H2 (text-xl font-semibold text-gray-900)</h2>
                <p className="text-xs text-gray-500">Major sections - 20px, 600 weight, primary text</p>
              </div>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">H3 (text-lg font-semibold text-gray-900)</h3>
                <p className="text-xs text-gray-500">Subsections - 18px, 600 weight, primary text</p>
              </div>
              <Separator />
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-2">H4 (text-base font-medium text-gray-900)</h4>
                <p className="text-xs text-gray-500">Minor headers, table row headers with avatars - 15px, 500 weight, primary text</p>
              </div>
              <Separator />
              <div>
                <p className="text-base text-gray-900 mb-2">Body (text-base font-normal text-gray-900)</p>
                <p className="text-xs text-gray-500">Default text - 15px, 400 weight, primary text</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-gray-900 mb-2">Caption (text-sm font-medium text-gray-900)</p>
                <p className="text-xs text-gray-500">Helper text, metadata, table rows (except headers) - 14px, 400-500 weight, primary text</p>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-gray-500 mb-2">Small (text-xs font-normal text-gray-500)</p>
                <p className="text-xs text-gray-500">Fine print, timestamps - 12px, 400 weight, muted text</p>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Font Weights</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-3">
              <p className="font-bold text-gray-900">Bold (700) - Display text</p>
              <p className="font-semibold text-gray-900">Semibold (600) - H1-H3 headers</p>
              <p className="font-medium text-gray-900">Medium (500) - H4, emphasis</p>
              <p className="font-normal text-gray-900">Normal (400) - Default body text</p>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Text Colors</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-3">
              <p className="text-gray-900">text-gray-900 (#0f172a Dark Slate) - Primary text, default for all body text and headings</p>
              <p className="text-gray-700">text-gray-700 (#374151) - Secondary text, labels</p>
              <p className="text-gray-500">text-gray-500 (#6B7280) - Muted text, help text</p>
              <Separator />
              <p style={{ color: '#B91C1C' }}>#B91C1C - Error states (6.71:1 contrast)</p>
              <p style={{ color: '#047857' }}>#047857 - Success states (5.77:1 contrast)</p>
              <p style={{ color: '#A16207' }}>#A16207 - Warning states (4.76:1 contrast)</p>
              <p className="text-[#290BD9]">Cobalt (#290BD9) - Info, links, and primary brand color</p>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Links</h2>
          <Card className="rounded-lg border bg-white">
            <CardHeader className="p-6 pb-1">
              <CardTitle className="text-lg font-semibold text-gray-900">Link Styling</CardTitle>
              <p className="text-gray-900">All links use Cobalt (#290BD9) with darker hover state (#2309C5)</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Inline Text Link</p>
                <p className="text-xs text-gray-500 mb-3">Standard link within body text - underlined for visibility</p>
                <p className="text-gray-900">
                  Read our <a href="#" className="text-[#290BD9] hover:text-[#2309C5] underline underline-offset-2">terms of service</a> and <a href="#" className="text-[#290BD9] hover:text-[#2309C5] underline underline-offset-2">privacy policy</a> for more details.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  <code className="px-2 py-1 bg-gray-100 rounded">text-[#290BD9] hover:text-[#2309C5] underline underline-offset-2</code>
                </p>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Link Without Underline</p>
                <p className="text-xs text-gray-500 mb-3">Use sparingly - underline appears on hover</p>
                <p className="text-gray-900">
                  View <a href="#" className="text-[#290BD9] hover:text-[#2309C5] hover:underline">campaign details</a> or <a href="#" className="text-[#290BD9] hover:text-[#2309C5] hover:underline">download report</a>
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  <code className="px-2 py-1 bg-gray-100 rounded">text-[#290BD9] hover:text-[#2309C5] hover:underline</code>
                </p>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Link with Icon</p>
                <p className="text-xs text-gray-500 mb-3">External links or links that open new windows</p>
                <p className="text-gray-900">
                  Visit our <a href="#" className="text-[#290BD9] hover:text-[#2309C5] inline-flex items-center gap-1">Help Center <ExternalLink className="h-3 w-3" /></a> for support resources.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  <code className="px-2 py-1 bg-gray-100 rounded">text-[#290BD9] hover:text-[#2309C5] inline-flex items-center gap-1</code>
                </p>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Link in Table Cell</p>
                <p className="text-xs text-gray-500 mb-3">Clickable text in data tables - no underline needed</p>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Summer Sale 2025</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Active</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a href="#" className="text-[#290BD9] hover:text-[#2309C5] text-sm font-medium">View Details</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <code className="px-2 py-1 bg-gray-100 rounded">text-[#290BD9] hover:text-[#2309C5] text-sm font-medium</code>
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Typography in Practice</h2>
          <Card className="rounded-lg border bg-white">
            <CardHeader className="p-6 pb-1">
              <CardTitle className="text-2xl font-semibold text-gray-900">The Evolution of Digital Marketing in the AI Era</CardTitle>
              <p className="text-gray-900">
                Digital marketing has undergone a <b>revolutionary transformation</b> over the past decade, fundamentally reshaping how businesses connect with their audiences. The integration of artificial intelligence and machine learning technologies has created unprecedented opportunities for marketers to deliver personalized, data-driven campaigns at scale.
              </p>
            </CardHeader>
            <CardContent>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Understanding Modern Consumer Behavior</h2>
              <p className="text-gray-900 mb-4">
                Today's consumers are more informed and empowered than ever before. They expect seamless experiences across multiple touchpoints, from social media platforms to e-commerce websites. <i>The traditional marketing funnel has evolved into a complex web of interactions</i>, where customers may engage with brands through various channels before making a purchase decision.
              </p>

              <blockquote className="border-l-4 border-[#290BD9] pl-4 py-2 my-4 bg-gray-50 rounded-r-lg">
                <p className="text-gray-900 italic">"The future of marketing lies not in reaching everyone, but in reaching the right person at the right time with the right message."</p>
              </blockquote>

              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Strategies for Success</h3>
              <p className="text-gray-900">
                Successful digital marketing campaigns in the modern landscape require a strategic approach that balances creativity with data analytics. Here are the essential elements that drive results:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-900 mt-3">
                <li>Comprehensive audience segmentation and persona development</li>
                <li>Multi-channel attribution modeling to understand customer journeys</li>
                <li>Real-time optimization based on performance metrics</li>
                <li>Content personalization at scale using AI technologies</li>
                <li>Integration of first-party data for privacy-compliant targeting</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">The Role of Automation</h3>
              <p className="text-gray-900">
                Marketing automation has become <b>essential for efficiency</b> in today's fast-paced digital environment. However, it's crucial to understand that automation should enhance human creativity, not replace it. The most successful campaigns combine automated processes with strategic human oversight.
              </p>
              <ol className="list-decimal list-inside space-y-2 text-gray-900 mt-3">
                <li>Identify repetitive tasks that can be automated</li>
                <li>Implement testing frameworks for continuous improvement</li>
                <li>Monitor performance metrics and adjust strategies accordingly</li>
                <li>Maintain human oversight for quality control and creative direction</li>
                <li>Scale successful campaigns while preserving brand authenticity</li>
              </ol>

              <h2 className="text-xl font-semibold text-gray-900 mb-4 mt-6">Data Privacy and Ethical Marketing</h2>
              <p className="text-gray-900 mb-4">
                As data collection capabilities have expanded, so too have concerns about privacy and ethical use of consumer information. <s>The old approach of aggressive data collection</s> has given way to a more thoughtful, permission-based model that respects user privacy while still delivering value.
              </p>
              <p className="text-gray-900 mb-4">
                Modern marketers must navigate a complex regulatory landscape, including GDPR, CCPA, and other privacy frameworks. This shift has actually created opportunities for brands to <b>build trust</b> by demonstrating transparency and respect for consumer preferences.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Building Customer Relationships</h3>
              <p className="text-gray-900">
                The ultimate goal of digital marketing extends beyond immediate conversions. <i>Long-term customer relationships</i> drive sustainable growth and create brand advocates who amplify marketing messages organically. This requires:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-900 mt-3">
                <li>Consistent brand messaging across all channels</li>
                <li>Responsive customer service and engagement</li>
                <li>Value-driven content that educates and informs</li>
                <li>Community building through social platforms</li>
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mb-4 mt-6">Looking Ahead</h2>
              <p className="text-gray-900 mb-4">
                The future of digital marketing will be shaped by emerging technologies like augmented reality, voice search optimization, and advanced AI capabilities. However, the fundamental principles remain constant: <b>understand your audience</b>, <b>deliver value</b>, and <b>build authentic connections</b>.
              </p>
              <p className="text-gray-900 mb-4">
                Success in this dynamic landscape requires continuous learning, adaptation, and a willingness to experiment with new approaches while maintaining focus on measurable outcomes. Those who can balance innovation with proven strategies will thrive in the evolving digital marketplace.
              </p>

              <p className="text-sm text-gray-700 mt-6 pt-4 border-t border-gray-200">
                <i>This article demonstrates the application of typography standards including headers, body text, bold emphasis, italics, blockquotes, ordered and unordered lists, and strikethrough formatting.</i>
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
